import { useNavigate } from 'react-router-dom';
import '../styles/LegalPage.css';

function DataProtection() {
    const navigate = useNavigate();

    return (
        <div className="legal-page">
            <div className="legal-container">
                <button className="legal-back" onClick={() => navigate(-1)}>
                    &larr; Back
                </button>

                <h1>Privacy Policy</h1>
                <p className="legal-updated">Last Updated: April 27, 2026</p>

                <section>
                    <h2>1. Introduction</h2>
                    <p>
                        Welcome to PrimeMaxing ("we," "us," or "our"). PrimeMaxing operates a health information
                        platform (the "Service") that allows users to upload lab test results, view
                        biomarker insights, and receive personalized wellness information. The Service
                        may also integrate with third-party health and fitness applications you authorize
                        to provide a more comprehensive view of your health data.
                    </p>
                    <p>
                        This Privacy Policy describes how we collect, use, disclose, and protect your information
                        when you use our Service. By accessing or using the Service, you agree to this Privacy Policy.
                    </p>
                </section>

                <section>
                    <h2>2. Important Disclaimers</h2>
                    <p>
                        <strong>PrimeMaxing is not a healthcare provider.</strong> The Service is an informational
                        tool designed to help you understand your lab results. The scores, insights, and
                        suggestions provided by the Service are generated by algorithms and artificial
                        intelligence and are <strong>not medical advice, diagnoses, or treatment plans</strong>.
                    </p>
                    <p>
                        While we may offer the ability to connect with licensed healthcare providers through
                        our platform, these providers are independent third parties. Any medical advice,
                        diagnoses, or treatment you receive from a healthcare provider through or in connection
                        with the Service is the sole responsibility of that provider.
                    </p>
                    <p>
                        <strong>Always consult a qualified healthcare professional</strong> before making any
                        health decisions based on information provided by the Service. Do not disregard
                        professional medical advice or delay seeking it because of information you received
                        through PrimeMaxing.
                    </p>
                </section>

                <section>
                    <h2>3. Information We Collect</h2>

                    <h3>3.1 Account Information</h3>
                    <p>When you create an account, we collect:</p>
                    <ul>
                        <li>Email address</li>
                        <li>Name (first and last)</li>
                        <li>Date of birth</li>
                        <li>Biological sex</li>
                        <li>Race/ethnicity</li>
                        <li>Menopausal status (if applicable)</li>
                    </ul>

                    <h3>3.2 Health &amp; Lab Data</h3>
                    <p>When you use the Service, we collect and process:</p>
                    <ul>
                        <li>Lab test results uploaded by you (PDF, CSV, or JSON formats)</li>
                        <li>Parsed biomarker values extracted from your lab reports</li>
                        <li>Organ system health scores generated from your data</li>
                        <li>Supplement and intervention suggestions generated for you</li>
                        <li>Protocol and compliance tracking data</li>
                    </ul>

                    <h3>3.3 Third-Party App Data</h3>
                    <p>
                        With your explicit permission, the Service may access data from third-party
                        health and fitness applications you connect, such as fitness trackers,
                        menstrual cycle trackers, sleep monitors, and similar apps. This may include:
                    </p>
                    <ul>
                        <li>Biometric data (heart rate, sleep patterns, activity levels)</li>
                        <li>Reproductive health data (cycle tracking, fertility indicators)</li>
                        <li>Nutrition and body composition data</li>
                        <li>Other health metrics shared by the connected application</li>
                    </ul>
                    <p>
                        We only access data from third-party apps when you explicitly grant
                        permission, and you may revoke access at any time. We do not share your
                        third-party app data with other services or use it for purposes beyond
                        providing the Service to you.
                    </p>

                    <h3>3.4 Usage Information</h3>
                    <p>We automatically collect certain information about your use of the Service, including:</p>
                    <ul>
                        <li>Device and browser information</li>
                        <li>IP address</li>
                        <li>Pages visited and features used</li>
                        <li>Interaction data with the conversational AI assistant</li>
                    </ul>
                </section>

                <section>
                    <h2>4. How We Use Your Information</h2>
                    <p>We use the information we collect to:</p>
                    <ul>
                        <li>Provide, maintain, and improve the Service</li>
                        <li>Analyze your lab results and generate health scores and suggestions</li>
                        <li>Personalize your experience based on your profile (age, sex, race)</li>
                        <li>Enable doctor-patient data sharing when you grant access</li>
                        <li>Power the AI conversational assistant to answer your health questions</li>
                        <li>Communicate with you about the Service, including updates and support</li>
                        <li>Comply with legal obligations</li>
                    </ul>
                </section>

                <section>
                    <h2>5. AI-Generated Content</h2>
                    <p>
                        The Service uses artificial intelligence and algorithmic analysis to process your lab
                        data, generate health scores, produce suggestions, and power our conversational
                        assistant. These AI-generated outputs are for <strong>informational purposes only</strong> and
                        should not be considered medical advice.
                    </p>
                    <p>
                        AI systems can make errors. The scores, insights, and suggestions may not account
                        for your complete medical history, current medications, allergies, or other factors
                        that a healthcare provider would consider. You should always verify AI-generated
                        information with a qualified healthcare professional.
                    </p>
                </section>

                <section>
                    <h2>6. How We Share Your Information</h2>
                    <p>We do not sell your personal health information. We may share your information in the following circumstances:</p>

                    <h3>6.1 With Your Consent</h3>
                    <p>
                        When you grant a healthcare provider or peer access to your data through the platform's
                        sharing features, we will share the applicable data with those individuals.
                    </p>

                    <h3>6.2 Service Providers</h3>
                    <p>
                        We use third-party service providers to operate the Service, including cloud
                        infrastructure and authentication providers. These providers are
                        contractually obligated to protect your information and are subject to
                        appropriate data processing agreements.
                    </p>

                    <h3>6.3 Legal Requirements</h3>
                    <p>
                        We may disclose your information if required by law, regulation, legal process,
                        or governmental request, or to protect the rights, property, or safety of PrimeMaxing,
                        our users, or others.
                    </p>

                    <h3>6.4 De-Identified Data</h3>
                    <p>
                        We may use de-identified, aggregated data that cannot be traced back to you for
                        research, analytics, and Service improvement purposes.
                    </p>
                </section>

                <section>
                    <h2>7. Data Security</h2>
                    <p>
                        We implement industry-standard security measures to protect your data, including:
                    </p>
                    <ul>
                        <li>Encryption of data in transit and at rest using industry-standard protocols</li>
                        <li>Secure authentication with multi-factor authentication support</li>
                        <li>Role-based access controls</li>
                        <li>Periodic internal security reviews with plans for independent third-party audits</li>
                        <li>Isolated, access-controlled storage for user data</li>
                    </ul>
                    <p>
                        While we strive to protect your information, no method of electronic transmission
                        or storage is 100% secure. We cannot guarantee absolute security.
                    </p>
                </section>

                <section>
                    <h2>8. HIPAA Considerations</h2>
                    <p>
                        PrimeMaxing is not a covered entity under HIPAA. However, we are committed to
                        protecting your health information with safeguards that align with HIPAA principles.
                        When healthcare providers use our platform to view patient data, those providers
                        remain subject to their own HIPAA obligations.
                    </p>
                </section>

                <section>
                    <h2>9. Data Retention &amp; Deletion</h2>
                    <p>
                        We retain your data for as long as your account is active or as needed to provide
                        the Service. You may request deletion of your data at any time by contacting us.
                        Upon account deletion:
                    </p>
                    <ul>
                        <li>Your profile information will be deleted from our active systems</li>
                        <li>Your uploaded lab files will be removed from active storage and queued
                            for permanent deletion within 30 days</li>
                        <li>Generated scores and suggestions will be deleted</li>
                        <li>De-identified, aggregated data may be retained for research purposes</li>
                    </ul>
                    <p>
                        Deleted data may be recoverable for up to 30 days to protect against
                        accidental loss. After that period, data is permanently purged from our
                        systems. Access audit logs that record system activity (but not your health
                        data itself) are retained separately for compliance purposes and cannot be
                        deleted.
                    </p>
                </section>

                <section>
                    <h2>10. Your Rights</h2>
                    <p>Depending on your jurisdiction, you may have the right to:</p>
                    <ul>
                        <li><strong>Access</strong> — Request a copy of the personal information we hold about you</li>
                        <li><strong>Correction</strong> — Request correction of inaccurate information</li>
                        <li><strong>Deletion</strong> — Request deletion of your personal information</li>
                        <li><strong>Portability</strong> — Request your data in a portable format</li>
                        <li><strong>Opt-Out</strong> — Opt out of marketing communications</li>
                        <li><strong>Restriction</strong> — Request that we limit processing of your data</li>
                    </ul>
                    <p>
                        To exercise any of these rights, please contact us at the address below.
                    </p>
                </section>

                <section>
                    <h2>11. Cookies &amp; Browser Storage</h2>
                    <p>
                        PrimeMaxing does <strong>not use cookies</strong> and does not employ third-party
                        tracking technologies such as analytics pixels or advertising trackers.
                    </p>
                    <p>
                        We do use your browser's built-in storage mechanisms (localStorage and sessionStorage)
                        for strictly functional purposes, including:
                    </p>
                    <ul>
                        <li>Maintaining your authenticated session</li>
                        <li>Caching lab data and reference information for performance</li>
                        <li>Storing your preferences (e.g., layout ordering, notification settings)</li>
                        <li>Tracking protocol compliance progress locally on your device</li>
                    </ul>
                    <p>
                        This data is stored only in your browser, is not transmitted to third parties,
                        and can be cleared at any time through your browser settings. Session-based
                        storage is automatically cleared when you close your browser tab.
                    </p>
                </section>

                <section>
                    <h2>12. Children's Privacy</h2>
                    <p>
                        Individuals under 18 years of age may not create accounts or use the Service
                        directly. We do not knowingly collect personal information from children through
                        self-registration. If we learn that a minor has created an account without
                        authorization, we will take steps to delete it promptly.
                    </p>
                    <p>
                        <strong>Provider-Managed Minor Accounts.</strong> Licensed healthcare providers
                        using the Service may add patients under 18 to the platform as part of their
                        clinical practice. In such cases, the provider is solely responsible for obtaining
                        all required parental or guardian consent before adding the minor's data to the
                        Service, and for complying with all applicable laws governing the collection and
                        use of minors' health information, including COPPA, HIPAA, and relevant state
                        regulations. PrimeMaxing processes minor patient data on behalf of and at the
                        direction of the provider.
                    </p>
                </section>

                <section>
                    <h2>13. Third-Party Links</h2>
                    <p>
                        The Service may contain links to third-party websites or services. We are not
                        responsible for the privacy practices of those third parties. We encourage you to
                        review their privacy policies.
                    </p>
                </section>

                <section>
                    <h2>14. Changes to This Policy</h2>
                    <p>
                        We may update this Privacy Policy from time to time. We will notify you of material
                        changes by posting the updated policy on the Service and updating the "Last Updated"
                        date. Your continued use of the Service after changes constitutes acceptance of the
                        updated policy.
                    </p>
                </section>

                <section>
                    <h2>15. Contact Us</h2>
                    <p>
                        If you have questions about this Privacy Policy or wish to exercise your rights,
                        please contact us at:
                    </p>
                    <p>
                        <strong>PrimeMaxing</strong><br />
                        Email: privacy@primemaxing.com
                    </p>
                </section>
            </div>
        </div>
    );
}

export default DataProtection;
